import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Search,
  Presentation,
  MessageCircle,
  FileText,
  BarChart3,
  Image,
  Plus,
  BookOpen,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
interface ChatActionMenuProps {
  onSelect: (skillName: string) => void
  disabled?: boolean
}

const BUILTIN_ACTIONS: { name: string; label: string; description: string; icon: typeof Search }[] = [
  { name: 'research', label: 'Recherche', description: 'Web-Recherche zu einem Thema', icon: Search },
  { name: 'memory', label: 'Wissen', description: 'Wiki-Speicher abfragen oder erweitern', icon: BookOpen },
  { name: 'presentation', label: 'Präsentation', description: 'PDF/HTML-Deck aus Quellen', icon: Presentation },
  { name: 'linkedin', label: 'LinkedIn', description: 'Post oder Karussell', icon: MessageCircle },
  { name: 'x', label: 'X / Thread', description: 'Post oder Thread', icon: MessageCircle },
  { name: 'newsletter', label: 'Newsletter', description: 'E-Mail-Newsletter', icon: FileText },
  { name: 'blog', label: 'Blog', description: 'Blog-Artikel', icon: FileText },
  { name: 'data-analysis', label: 'Datenanalyse', description: 'Zahlen aus Quellen', icon: BarChart3 },
  { name: 'image', label: 'Bild', description: 'Bild aus Prompt', icon: Image },
]

const GROUPS = [
  { id: 'research', label: 'Recherche', names: ['research'] },
  { id: 'knowledge', label: 'Wissen', names: ['memory'] },
  { id: 'output', label: 'Outputs', names: ['presentation', 'linkedin', 'x', 'newsletter', 'blog'] },
  { id: 'analysis', label: 'Analyse & Bild', names: ['data-analysis', 'image'] },
]

export function ChatActionMenu({ onSelect, disabled }: ChatActionMenuProps) {
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])

  const flatActions = useMemo(() => BUILTIN_ACTIONS, [])

  const grouped = useMemo(() => {
    return GROUPS.map((g) => ({
      ...g,
      actions: flatActions.filter((a) => g.names.includes(a.name)),
    })).filter((g) => g.actions.length > 0)
  }, [flatActions])

  // Reset active index when popover opens
  useEffect(() => {
    if (open) setActiveIndex(0)
  }, [open])

  // Focus active item when it changes
  useEffect(() => {
    if (!open) return
    itemRefs.current[activeIndex]?.focus()
  }, [activeIndex, open])

  function handleSelect(name: string) {
    onSelect(name)
    setOpen(false)
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!open) return
    const count = flatActions.length
    if (count === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((prev) => (prev + 1) % count)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((prev) => (prev - 1 + count) % count)
    } else if (e.key === 'Home') {
      e.preventDefault()
      setActiveIndex(0)
    } else if (e.key === 'End') {
      e.preventDefault()
      setActiveIndex(count - 1)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const action = flatActions[activeIndex]
      if (action) handleSelect(action.name)
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  let globalIndex = 0

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-11 w-11 shrink-0 min-[761px]:h-9 min-[761px]:w-9"
          disabled={disabled}
          aria-label="Aktionen öffnen"
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <Plus className="h-5 w-5 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="top"
        className="w-64 max-h-[min(calc(100dvh-2rem),var(--radix-popover-content-available-height))] overflow-y-auto p-2"
        onKeyDown={handleKeyDown}
        role="menu"
        aria-label="Chat-Aktionen"
      >
        <div className="space-y-2">
          <p className="px-2 text-xs font-medium text-muted-foreground">Aktion wählen</p>
          {grouped.map((group) => (
            <div key={group.id}>
              <p
                className="px-2 py-1 text-[12px] font-semibold uppercase tracking-wider text-muted-foreground"
                aria-hidden="true"
              >
                {group.label}
              </p>
              <div className="space-y-0.5" role="group" aria-label={group.label}>
                {group.actions.map((action) => {
                  const Icon = action.icon
                  const index = globalIndex++
                  const isActive = index === activeIndex
                  return (
                    <button
                      key={action.name}
                      ref={(el) => { itemRefs.current[index] = el }}
                      type="button"
                      role="menuitem"
                      tabIndex={isActive ? 0 : -1}
                      onClick={() => handleSelect(action.name)}
                      onMouseEnter={() => setActiveIndex(index)}
                      className="flex min-h-11 min-[761px]:min-h-9 w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted focus:bg-muted focus:outline-none"
                    >
                      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                      <div className="min-w-0">
                        <p className="font-medium text-foreground">{action.label}</p>
                        <p className="text-[12px] text-muted-foreground">{action.description}</p>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
